/** Google Identity Services ("Sign in with Google"): the script is loaded on demand, once per page. */

export interface GoogleCredentialResponse {
  /** the ID token (JWT) the backend verifies */
  credential: string;
}

export interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    /** hint for the account chooser only; the backend checks the domain on the token */
    hd?: string;
    ux_mode?: "popup" | "redirect";
    auto_select?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type?: "standard" | "icon";
      theme?: "outline" | "filled_blue" | "filled_black";
      size?: "large" | "medium" | "small";
      text?: "signin_with" | "signup_with" | "continue_with" | "signin";
      shape?: "rectangular" | "pill" | "circle" | "square";
      logo_alignment?: "left" | "center";
      /** pixels, 200 to 400 */
      width?: number;
      locale?: string;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

const SCRIPT_URL = "https://accounts.google.com/gsi/client";

let loading: Promise<GoogleAccountsId> | null = null;

export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  const ready = window.google?.accounts?.id;
  if (ready) {
    return Promise.resolve(ready);
  }
  if (!loading) {
    loading = new Promise<GoogleAccountsId>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        const id = window.google?.accounts?.id;
        if (id) {
          resolve(id);
        } else {
          reject(new Error("Google Identity Services did not initialise"));
        }
      };
      script.onerror = () => {
        // a later attempt (another visit of the login page) tries again
        loading = null;
        script.remove();
        reject(new Error("Google Identity Services could not be loaded"));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
}
